<script setup>
import { useAxios } from '@/hooks/useAxios';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

// Props 정의
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    nonButton: {
        type: Boolean,
        default: false
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const isReadonly = computed(() => props.readonly);
const inputValue = ref(props.modelValue);
const isVerified = ref(false);
const ValidationMsg = ref('');
const RightMsg = ref('');
const errMessage = ref(props.errmsg);

onMounted(async () => {
    // await Promise.all([searchDriver()]);
    // nextTick(() => {
    //     filteredOptions.value = driverData.value;
    // });
});

watch(
    () => props.modelValue,
    (newVal) => {
        if (props.upper) {
            newVal = newVal.toUpperCase();
        }

        inputValue.value = newVal || '';
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const onInput = (event) => {
    const oldValue = event.target.value;
    const oldLength = oldValue.length;
    let cursorPosition = event.target.selectionStart;
    let value = event.target.value;
    let validationResult = { isValid: true, message: '' };
    ValidationMsg.value = '';
    errMessage.value = '';
    RightMsg.value = '';
    isVerified.value = false;

    if (value.length > props.maxlength) {
        value = value.slice(0, props.maxlength);
    }

    const beforeFormatLength = value.length;
    const formattedValue = formatBusinessNumber(value);
    const hyphenPositions = [3, 6].filter((pos) => formattedValue[pos] === '-');

    if (hyphenPositions.length > 0 && cursorPosition === hyphenPositions[0] + 1) {
        cursorPosition = cursorPosition + 1;
    }

    if (hyphenPositions.length > 1 && cursorPosition === hyphenPositions[1] + 1) {
        cursorPosition = cursorPosition + 1;
    }

    value = formattedValue;
    //cursorPosition = value.length;

    const afterFormatLength = value.length;
    const lengthDiff = afterFormatLength - beforeFormatLength;

    cursorPosition = Math.min(cursorPosition, afterFormatLength);

    if (typeof props.validationCheck === 'function') {
        try {
            validationResult = props.validationCheck(value) || validationResult;
        } catch (error) {
            console.error('Validation check error:', error);
        }
    }

    ValidationMsg.value = validationResult.isValid ? ValidationMsg.value : validationResult.message;
    emitUpdate(value);

    nextTick(() => {
        event.target.value = value;
        event.target.setSelectionRange(cursorPosition, cursorPosition);
    });
};

const emitUpdate = (value) => {
    inputValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        value: value
    });
};

const checkDuplicate = async () => {
    try {
        if (!validateBusinessNumber(inputValue.value)) return;

        const businessNo = inputValue.value?.replaceAll('-', '');
        const url = `/api/customer/businessNo/duplicate/${businessNo}`;
        const config = {
            method: 'get'
        };
        const { execute, setHeader } = useAxios(url, config, {
            immediate: false,
            onSuccess(response) {
                //console.log(response.data.data);
                if (response.data?.success) {
                    validateBusinessNoAPI(businessNo);
                    //isVerified.value = true;
                    //RightMsg.value = '유효한 사업자등록번호입니다.';
                }
            },
            onError(err) {
                if (err.response.data?.data) {
                    ValidationMsg.value = err.response.data?.data.business_no;
                    isVerified.value = false;
                } else console.error('Error:', err.message);
            }
        });
        setHeader('Content-Type', 'application/json');
        execute();
    } catch (error) {
        console.error('사업자등록번호 확인 중 오류 발생:', error);

        ValidationMsg.value = '사업자등록번호 확인 중 오류가 발생했습니다.';
    }
};

const validateBusinessNoAPI = async (businessNo) => {
    try {
        const url = `/api/company/businessNo/validate/${businessNo}`;
        const config = {
            method: 'get'
        };
        const { execute, setHeader } = useAxios(url, config, {
            immediate: false,
            onSuccess(response) {
                if (response.data?.data.is_validate) {
                    isVerified.value = true;
                    RightMsg.value = '유효한 사업자등록번호입니다.';
                    ValidationMsg.value = '';
                } else {
                    ValidationMsg.value = '사업자등록번호 검증에 실패했습니다.';
                }
            },
            onError(err) {
                ValidationMsg.value = '사업자등록번호 검증에 실패했습니다.';
                console.error('Validation API Error:', err.message);
            }
        });

        setHeader('Content-Type', 'application/json');
        execute();
    } catch (error) {
        console.error('사업자등록번호 검증 API 호출 중 오류 발생:', error);
        ValidationMsg.value = '사업자등록번호 검증 중 오류가 발생했습니다.';
    }
};

const validateBusinessNumber = (businessNo) => {
    // 하이픈 제거
    const cleanNo = businessNo.replace(/-/g, '');

    // 길이 체크
    if (cleanNo.length !== 10) {
        ValidationMsg.value = '사업자등록번호는 10자리여야 합니다.';
        return false;
    }

    // 숫자만 포함되어 있는지 체크
    if (!/^\d+$/.test(cleanNo)) {
        ValidationMsg.value = '사업자등록번호는 숫자만 포함해야 합니다.';
        return false;
    }

    ValidationMsg.value = '';
    return true;
};

const formatBusinessNumber = (value) => {
    value = value.replace(/[^0-9]/g, '');

    if (value.length <= 3) {
        return value;
    } else if (value.length <= 5) {
        return value.slice(0, 3) + '-' + value.slice(3);
    } else {
        return value.slice(0, 3) + '-' + value.slice(3, 5) + '-' + value.slice(5, 10);
    }
};

const clearInput = () => {
    emitUpdate('', 'input');
};

defineExpose({
    isVerified
});
</script>

<template>
    <div
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
            <span v-if="!ValidationMsg && RightMsg" class="right-message">{{ RightMsg }}</span>
        </label>

        <div class="relative flex flex-col items-center w-full">
            <div class="relative flex items-center w-full h-12">
                <input
                    v-if="visible"
                    type="text"
                    :id="inputId"
                    v-model="inputValue"
                    class="block w-full p-3 ps-5 input-font text-gray-900 border border-gray-300 rounded-lg bg-gray-60 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    :class="{
                        'error-border': ValidationMsg
                    }"
                    required
                    placeholder="111-22-33333"
                    :required="required"
                    @input="onInput"
                />

                <div class="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center">
                    <button
                        v-if="inputValue && !isReadonly"
                        @click="clearInput"
                        class="clear-button"
                        :class="{
                            'mr-[calc(6.5rem)]': !nonButton,
                            'mr-[calc(1.5rem)]': nonButton
                        }"
                    >
                        &times;
                    </button>
                    <button v-if="!nonButton" @click="checkDuplicate" class="button-style">확인</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus {
    border-color: #0038ea;
    outline: none;
}

.text-right {
    text-align: right;
    padding-right: 6rem;
}

.error-border {
    border: 1px solid red !important;
}
.custom-input.p-inputtext {
    padding: 0.75rem; /* 기본 높이 */
    border: 1px solid #d1d5db; /* 기본 테두리 색상 */
    border-radius: 0.5rem; /* 기본 border-radius */
    /* transition:
        border-color 0.3s ease,
        box-shadow 0.3s ease,
        height 0.3s ease; 애니메이션 */
}

.custom-input.p-inputtext:focus {
    border-color: #0038ea !important;
    /* border-radius: 0.5rem !important;*/
    /* height: 2.75rem !important; 포커스 시 높이 증가 */
    /* outline: none !important; 포커스 시 기본 outline 제거 */
}

.suggestions {
    position: absolute;
    list-style-type: none;
    padding: 0;
    margin: 0;
    border: 1px solid #ccc;
    border-top: none;
    max-height: 150px;
    overflow-y: auto;
    width: 100%;
    top: 100%; /* 입력 필드 바로 아래에 위치 */
    left: 0;
    background-color: white;
    z-index: 10;
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.suggestions li {
    padding: 8px 16px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.suggestions li:hover {
    background-color: #f0f0f0;
}

.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    transition: color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f1f1;
    color: #4b5563;
    align-items: center;
    position: absolute;
    right: 0.5rem;
    width: 5.5rem;
    height: 1.8rem;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.right-message {
    color: green;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.unit-text {
    font-family: 'SCoreDream500';
    font-size: 0.8rem;
    color: #4b5563;
    height: 100%;
    display: flex;
    align-items: center;
    position: absolute;
    padding-top: 0.2rem;
    right: 2.1rem;
    width: 2rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 1rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

.search-input {
    width: 100%;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}
</style>
